
import React, { createContext, useContext, useState } from "react";
import api from "../api/axios";
import { paymentsApi } from "../api/payments";
import { toNumericAmount } from "../utils/currencyUtils";
import { loadRazorpay } from "../utils/loadRazorpay";

const PaymentContext = createContext();

export const usePayment = () => useContext(PaymentContext);

export const PaymentProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);

  // Create Razorpay order (installment or full)
  const createOrder = async ({
    amount,
    courseId,
    planType,
    installmentPlanId,
    paymentMode,
    installmentIndex = null,
    totalInstallments = null,
    couponCode = null,
    currency = "INR",
  }) => {
    try {
      setLoading(true);
      const numericAmount = toNumericAmount(amount);
      if (!Number.isFinite(numericAmount)) {
        throw new Error("Invalid payment amount. Please refresh and try again.");
      }
      const payload = {
        amount: numericAmount,
        currency,
        courseId,
        planType,
        installmentPlanId,
        paymentMode,
        ...(paymentMode === "installment" && {
          installmentIndex,
          totalInstallments,
        }),
        couponCode,
      };

      const { data } = await api.post(paymentsApi.order, payload);
      setLoading(false);
      return data.order;
    } catch (error) {
      setLoading(false);
      console.error("❌ Error creating order:", error);
      const msg =
        error.response?.data?.message ||
        error.message ||
        "Could not start payment. Please try again.";
      throw new Error(msg);
    }
  };

  // Razorpay frontend-only payment handler
  const initiatePayment = async (order) => {
    // 🔥 NEW: Handle Free (0 amount) orders without Razorpay modal
    if (order.amount === 0) {
      console.log("🎟️ Free order detected, bypassing Razorpay modal");
      return {
        paymentId: order.receipt || `FREE_${Date.now()}`,
        orderId: order.orderId || order.id,
        signature: "FREE_ORDER_SIGNATURE",
      };
    }

    await loadRazorpay();

    return new Promise((resolve, reject) => {
      if (!window.Razorpay) {
        alert("Razorpay SDK not loaded. Please refresh the page.");
        return reject(new Error("Razorpay SDK not loaded"));
      }

      const options = {
        // ✅ SAFE: Razorpay key_id is meant to be public (client-side)
        // Only key_secret must remain server-side (never exposed to frontend)
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: order.amount,
        currency: order.currency,
        order_id: order.orderId,
        handler: (response) => {
          console.log("✅ Payment Success:", {
            paymentId: response.razorpay_payment_id,
            orderId: response.razorpay_order_id,
            signature: response.razorpay_signature,
          });

          resolve({
            paymentId: response.razorpay_payment_id,
            orderId: response.razorpay_order_id,
            signature: response.razorpay_signature,
          });
        },
        modal: {
          ondismiss: () => {
            console.warn("❌ Payment cancelled by user");
            reject(new Error("Payment cancelled"));
          },
        },
        theme: { color: "#3399cc" },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    });
  };

  // Verify payment signature with backend
  const verifyPaymentSignature = async (paymentData) => {
    try {
      setLoading(true);
      const { data } = await api.post(
        paymentsApi.verify,
        {
          razorpay_order_id: paymentData.orderId,
          razorpay_payment_id: paymentData.paymentId,
          razorpay_signature: paymentData.signature,
        },
        { timeout: 90000 }
      );
      setLoading(false);
      return data;
    } catch (error) {
      setLoading(false);
      console.error("❌ Error verifying payment signature:", error);
      const msg =
        error.response?.data?.message ||
        error.message ||
        "Payment verification failed. Please contact support if money was deducted.";
      throw new Error(msg);
    }
  };

  // Fetch outstanding balance and installment info
  const getOutstandingBalance = async (courseId, userId) => {
    try {
      setLoading(true);
      const { data } = await api.get(
        `/installments/${courseId}/balance/${userId}`
      );
      setLoading(false);
      return data;
    } catch (error) {
      setLoading(false);
      console.error("❌ Error fetching outstanding balance:", error);
      throw error;
    }
  };

  return (
    <PaymentContext.Provider
      value={{
        createOrder,
        initiatePayment,
        verifyPaymentSignature,
        getOutstandingBalance,
        loading,
      }}
    >
      {children}
    </PaymentContext.Provider>
  );
};
